import { useAppDispatch } from "app/hooks";
import { logOut } from "app/store/action-creators/auth.actions";
import sessionTimeoutService from "data/services/session-timeout.service";
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

interface SessionTimeoutContextValue {
  timeRemaining: number;
  isWarningActive: boolean;
  extendSession: () => void;
  endSession: () => void;
}

const SessionTimeoutContext = createContext<SessionTimeoutContextValue | null>(
  null
);

export const useSessionTimeout = () => {
  const context = useContext(SessionTimeoutContext);
  if (!context) {
    throw new Error(
      "useSessionTimeout must be used within SessionTimeoutProvider"
    );
  }
  return context;
};

interface SessionTimeoutProviderProps {
  children: React.ReactNode;
}

export const SessionTimeoutProvider: React.FC<SessionTimeoutProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isWarningActive, setIsWarningActive] = useState(false);

  useEffect(() => {
    // Set up session timeout callbacks
    sessionTimeoutService.setCallbacks({
      onTimeout: handleSessionTimeout,
      onWarning: handleSessionWarning,
      onWarningClear: handleWarningClear,
    });

    // Start session
    sessionTimeoutService.startSession();

    // Update time remaining every second
    const interval = setInterval(() => {
      setTimeRemaining(sessionTimeoutService.getTimeRemaining());
    }, 1000);

    return () => {
      clearInterval(interval);
      sessionTimeoutService.endSession();
    };
  }, []);

  const handleSessionTimeout = () => {
    toast.error("Session expired. You will be logged out.");
    dispatch(logOut(navigate));
    sessionTimeoutService.endSession();
  };

  const handleSessionWarning = () => {
    setIsWarningActive(true);
    toast("Your session will expire soon. Please extend your session.", {
      duration: 10000,
    });
  };

  const handleWarningClear = () => {
    setIsWarningActive(false);
    toast.dismiss();
  };

  const extendSession = () => {
    sessionTimeoutService.extendSession();
  };

  const endSession = () => {
    sessionTimeoutService.endSession();
    dispatch(logOut(navigate));
  };

  const value: SessionTimeoutContextValue = {
    timeRemaining,
    isWarningActive,
    extendSession,
    endSession,
  };

  return (
    <SessionTimeoutContext.Provider value={value}>
      {children}
    </SessionTimeoutContext.Provider>
  );
};
